import React from "react";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import { PAGE_NAME, ROUTE } from "../../../../../constants/constants";
import {
    useChangeCredentialMutation,
    useForgotCredentialMutation,
    useMyCredentialsQuery,
    useResetCredentialMutation,
} from "../../../../../store/credentialsManagement/credentialsSlice";
import { formattedDateTime } from "../../../../../utils";
import { Skeleton } from "../../../../atoms/skeleton";
import { Input } from "../../../../atoms/input";
import { useLocation, useNavigate } from "react-router-dom";
import { payloadType, ResponseType } from "../../../../../types";
import { toast } from "react-toastify";
import { Loader } from "../../../../atoms/loader";
import { Icon } from "../../../../atoms/icon";
import { Dropdown } from "../../../../atoms/dropdown";

export const MyCredentials = () => {
    const pageClassName = "p-myCredentials";

    const navigate = useNavigate();
    const [myProjects, setMyProjects] = React.useState([]);

    const {
        data: credentials,
        isLoading,
        isSuccess,
        isError,
        error: notFoundError,
    } = useMyCredentialsQuery();
    const [changeCredentialAPI, { isLoading: isChangeCredentialLoading }] =
        useChangeCredentialMutation();
    const [forgotCredentialAPI, { isLoading: isForgotCredentialLoading }] =
        useForgotCredentialMutation();
    const [resetCredentialAPI, { isLoading: isResetCredentialLoading }] =
        useResetCredentialMutation();

    const query = useLocation();
    const searchParams = new URLSearchParams(query.search);
    const token = searchParams.get("token");
    const error = searchParams.get("error");

    const initialPayload: payloadType = {
        project_id: {
            value: "" as unknown as { label: string; value: string },
            error: "",
            isError: false,
        },
        password: {
            value: "",
            error: "",
            isError: false,
        },
        old_password: {
            value: "",
            error: "",
            isError: false,
        },
        password_confirmation: {
            value: "",
            error: "",
            isError: false,
        },
        email: {
            value: "",
            error: "",
            isError: false,
        },
    };

    const [showType, setShowType] = React.useState("");

    React.useEffect(() => {
        if (token) {
            setShowType("resetPassword");
            const project = JSON.parse(atob(token))?.project;
            setPayload({
                ...payload,
                project_id: {
                    value: {
                        label: project?.name,
                        value: project?.id,
                    } as unknown as { label: string; value: string },
                    error: "",
                    isError: false,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    React.useEffect(() => {
        if (error) {
            toast.error(error);
        }
    }, [error]);

    React.useEffect(() => {
        if (credentials?.result) {
            setMyProjects(
                credentials?.result.map((item: any) => {
                    return {
                        label: item?.project?.name,
                        value: item?.project?.id,
                    };
                })
            );
        }
    }, [credentials]);

    const [payload, setPayload] = React.useState(initialPayload);

    const changeHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const passwordRegex =
            /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
        if (["password", "old_password", "password_confirmation"].includes(name)) {
            if (!passwordRegex.test(value)) {
                if (value.length !== 0) {
                    setPayload({
                        ...payload,
                        [name]: {
                            value,
                            error:
                                "Password should be at least 8 characters long and should contain at least one uppercase, one lowercase, one number and one special character.",
                            isError: true,
                        },
                    });
                } else {
                    setPayload({
                        ...payload,
                        [name]: {
                            value,
                            error: "",
                            isError: false,
                        },
                    });
                }
            } else {
                setPayload({
                    ...payload,
                    [name]: {
                        value,
                        error: "",
                        isError: false,
                    },
                });
            }
        } else {
            setPayload({
                ...payload,
                [name]: {
                    value,
                    error: "",
                    isError: false,
                },
            });
        }
    };

    const changePasswordHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const toastId = toast.loading("Loading...");

        const formData = new FormData();
        formData.append("old_password", payload.old_password.value as string);
        formData.append("password", payload.password.value as string);
        formData.append(
            "password_confirmation",
            payload.password_confirmation.value as string
        );
        formData.append(
            "project_id",
            (payload.project_id.value as { label: string; value: string })
                .value as string
        );

        const response = (await changeCredentialAPI(formData)) as ResponseType;

        if (response?.data?.success) {
            setPayload(initialPayload);
            setShowType("");

            toast.update(toastId, {
                render: response?.data?.message,
                type: toast.TYPE.SUCCESS,
                isLoading: isLoading,
                autoClose: 2000,
            });
            navigate(ROUTE.MY_CREDENTIAL);
        } else {
            toast.update(toastId, {
                render: response?.error?.data?.message,
                type: toast.TYPE.ERROR,
                isLoading: isLoading,
                autoClose: 2000,
            });

            if (response?.error?.data?.errors) {
                Object.keys(response?.error?.data?.errors).forEach((key) => {
                    setPayload((prevState) => ({
                        ...prevState,
                        [key]: {
                            ...prevState[key],
                            error: response?.error?.data?.errors[key][0],
                            isError: true,
                        },
                    }));
                });
            } else {
                toast.error(response?.error?.message);
            }
        }
    };

    const forgotPasswordHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const toastId = toast.loading("Loading...");

        const formData = new FormData();
        formData.append("email", payload.email.value as string);
        formData.append(
            "project_id",
            (payload.project_id.value as { label: string; value: string })
                .value as string
        );

        const response = (await forgotCredentialAPI(formData)) as ResponseType;

        if (response?.data?.success) {
            setPayload(initialPayload);

            toast.update(toastId, {
                render: response?.data?.message,
                type: toast.TYPE.SUCCESS,
                isLoading: isLoading,
                autoClose: 2000,
            });
            navigate(ROUTE.MY_CREDENTIAL);
        } else {
            toast.update(toastId, {
                render: response?.error?.data?.message,
                type: toast.TYPE.ERROR,
                isLoading: isLoading,
                autoClose: 2000,
            });

            if (response?.error?.data?.errors) {
                Object.keys(response?.error?.data?.errors).forEach((key) => {
                    setPayload((prevState) => ({
                        ...prevState,
                        [key]: {
                            ...prevState[key],
                            error: response?.error?.data?.errors[key][0],
                            isError: true,
                        },
                    }));
                });
            } else {
                toast.error(response?.error?.message);
            }
        }
    };

    const resetPasswordHandler = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const toastId = toast.loading("Loading...");

        const formData = new FormData();

        formData.append("password", payload.password.value as string);
        formData.append(
            "password_confirmation",
            payload.password_confirmation.value as string
        );
        formData.append("token", token as string);
        formData.append(
            "project_id",
            (payload.project_id.value as { label: string; value: string })
                .value as string
        );

        const response = (await resetCredentialAPI(formData)) as ResponseType;

        if (response?.data?.success) {
            setPayload(initialPayload);
            setShowType("");

            toast.update(toastId, {
                render: response?.data?.message,
                type: toast.TYPE.SUCCESS,
                isLoading: isLoading,
                autoClose: 2000,
            });
            navigate(ROUTE.MY_CREDENTIAL);
        } else {
            toast.update(toastId, {
                render: response?.error?.data?.message,
                type: toast.TYPE.ERROR,
                isLoading: isLoading,
                autoClose: 2000,
            });

            if (response?.error?.data?.errors) {
                Object.keys(response?.error?.data?.errors).forEach((key) => {
                    setPayload((prevState) => ({
                        ...prevState,
                        [key]: {
                            ...prevState[key],
                            error: response?.error?.data?.errors[key][0],
                            isError: true,
                        },
                    }));
                });
            } else {
                toast.error(response?.error?.message);
            }
        }
    };

    const cancelHandler = () => {
        setShowType("");
        setPayload(initialPayload);
    };

    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>{PAGE_NAME.MY_CREDENTIAL}</p>

            {isSuccess ? (
                <div className={`${pageClassName}__container`}>
                    {credentials?.result?.map((item: any) => (
                        <div
                            key={item?.id}
                            className={`${pageClassName}__group__container`}
                        >
                            <div className={`${pageClassName}__group`}>
                                <p className={`${pageClassName}__group__project__label`}>
                                    Project
                                </p>
                                <p className={`${pageClassName}__group__project__value`}>
                                    {item?.project?.name}
                                </p>
                            </div>

                            <div className={`${pageClassName}__group`}>
                                <p className={`${pageClassName}__group__label`}>First Name</p>
                                <p className={`${pageClassName}__group__value`}>
                                    {item?.user?.firstName}
                                </p>
                            </div>
                            <div className={`${pageClassName}__group`}>
                                <p className={`${pageClassName}__group__label`}>Last Name</p>
                                <p className={`${pageClassName}__group__value`}>
                                    {item?.user?.lastName}
                                </p>
                            </div>
                            <div className={`${pageClassName}__group`}>
                                <p className={`${pageClassName}__group__label`}>Email</p>
                                <p className={`${pageClassName}__group__value`}>
                                    {item?.email}
                                </p>
                            </div>

                            <div className={`${pageClassName}__group`}>
                                <p className={`${pageClassName}__group__label`}>Password</p>
                                <p className={`${pageClassName}__group__password`}>
                                    {item?.password}
                                </p>
                            </div>

                            <div className={`${pageClassName}__group`}>
                                <p className={`${pageClassName}__group__label`}>
                                    Production Feed URL
                                </p>
                                <div className={`${pageClassName}__group__url`}>
                                    <p className={`${pageClassName}__group__url__value`}>
                                        {item?.project?.production_feed_url}
                                    </p>
                                    <button
                                        className={`${pageClassName}__group__url__button`}
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                item?.project?.production_feed_url
                                            );
                                            toast.success("URL copied to clipboard");
                                        }}
                                        type="button"
                                    >
                                        <Icon icon="copy" size="secondary" />
                                    </button>
                                </div>
                            </div>

                            <div className={`${pageClassName}__group`}>
                                <p className={`${pageClassName}__group__label`}>
                                    Development Feed URL
                                </p>
                                <div className={`${pageClassName}__group__url`}>
                                    <p className={`${pageClassName}__group__url__value`}>
                                        {item?.project?.development_feed_url}
                                    </p>
                                    <button
                                        className={`${pageClassName}__group__url__button`}
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                item?.project?.development_feed_url
                                            );
                                            toast.success("URL copied to clipboard");
                                        }}
                                        type="button"
                                    >
                                        <Icon icon="copy" size="secondary" />
                                    </button>
                                </div>
                            </div>

                            <div className={`${pageClassName}__group`}>
                                <p className={`${pageClassName}__group__label`}>Updated At</p>
                                <p className={`${pageClassName}__group__value`}>
                                    {formattedDateTime(item?.updatedAt)}
                                </p>
                            </div>
                        </div>
                    ))}
                    <div className={`${pageClassName}__action`}>
                        <div className={`${pageClassName}__group`}>
                            <p className={`${pageClassName}__group__label`} />
                            <div className={`${pageClassName}__group__action`}>
                                {isLoading ? (
                                    <Skeleton height="small" />
                                ) : (
                                    !showType && (
                                        <div
                                            className={`${pageClassName}__group__action__container`}
                                        >
                                            <button
                                                className={`${pageClassName}__group__action__button__change`}
                                                onClick={() => setShowType("changePassword")}
                                                type="button"
                                            >
                                                Change Password
                                            </button>
                                            <button
                                                className={`${pageClassName}__group__action__button__forgot`}
                                                onClick={() => setShowType("forgotPassword")}
                                                type="button"
                                            >
                                                Forgot Password
                                            </button>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                    <Loader
                        isLoading={
                            isChangeCredentialLoading ||
                            isForgotCredentialLoading ||
                            isResetCredentialLoading
                        }
                    >
                        <div className={`${pageClassName}__password`}>
                            {showType === "changePassword" && (
                                <form
                                    className={`${pageClassName}__form`}
                                    onSubmit={changePasswordHandler}
                                >
                                    <p className={`${pageClassName}__form__label`}>
                                        Change Password
                                    </p>
                                    <div className={`${pageClassName}__form__container`}>
                                        <div className={`${pageClassName}__form__group`}>
                                            <label className={`${pageClassName}__form__group__label`}>
                                                Project
                                            </label>
                                            <Dropdown
                                                placeholder="Select project"
                                                options={myProjects}
                                                onClickChange={(value) => {
                                                    setPayload({
                                                        ...payload,
                                                        project_id: {
                                                            value: value as { label: string; value: string },
                                                            error: "",
                                                            isError: false,
                                                        },
                                                    });
                                                }}
                                                selected={
                                                    payload.project_id.value as {
                                                        label: string;
                                                        value: string;
                                                    }
                                                }
                                                isError={payload.project_id.isError}
                                                error={payload.project_id.error}
                                            />
                                        </div>
                                        <Input
                                            name="old_password"
                                            label="Old Password"
                                            placeholder="Enter new password"
                                            onChange={changeHandler}
                                            value={payload.old_password.value as string}
                                            error={payload.old_password.error}
                                            isError={payload.old_password.isError}
                                        />
                                        <Input
                                            name="password"
                                            label="New Password"
                                            placeholder="Enter new password"
                                            onChange={changeHandler}
                                            value={payload.password.value as string}
                                            error={payload.password.error}
                                            isError={payload.password.isError}
                                        />
                                        <Input
                                            name="password_confirmation"
                                            label="Confirm Password"
                                            placeholder="Enter new password"
                                            onChange={changeHandler}
                                            value={payload.password_confirmation.value as string}
                                            error={payload.password_confirmation.error}
                                            isError={payload.password_confirmation.isError}
                                        />
                                        <div className={`${pageClassName}__form__action`}>
                                            <button
                                                className={`${pageClassName}__form__action__button ${pageClassName}__form__action__button--cancel`}
                                                type="button"
                                                onClick={cancelHandler}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className={`${pageClassName}__form__action__button`}
                                                type="submit"
                                            >
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            )}

                            {showType === "forgotPassword" && (
                                <form
                                    className={`${pageClassName}__form`}
                                    onSubmit={forgotPasswordHandler}
                                >
                                    <p className={`${pageClassName}__form__label`}>
                                        Forgot Password
                                    </p>
                                    <div className={`${pageClassName}__form__container`}>
                                        <div className={`${pageClassName}__form__group`}>
                                            <label className={`${pageClassName}__form__group__label`}>
                                                Project
                                            </label>
                                            <Dropdown
                                                placeholder="Select project"
                                                options={myProjects}
                                                onClickChange={(value) => {
                                                    setPayload({
                                                        ...payload,
                                                        project_id: {
                                                            value: value as { label: string; value: string },
                                                            error: "",
                                                            isError: false,
                                                        },
                                                    });
                                                }}
                                                selected={
                                                    payload.project_id.value as {
                                                        label: string;
                                                        value: string;
                                                    }
                                                }
                                                isError={payload.project_id.isError}
                                                error={payload.project_id.error}
                                            />
                                        </div>

                                        <Input
                                            name="email"
                                            type="email"
                                            label="Email"
                                            placeholder="Enter your email"
                                            onChange={changeHandler}
                                            value={payload.email.value as string}
                                            isError={payload.email.isError}
                                            error={payload.email.error}
                                            isRequired
                                            isStar
                                        />
                                        <div className={`${pageClassName}__form__action`}>
                                            <button
                                                className={`${pageClassName}__form__action__button ${pageClassName}__form__action__button--cancel`}
                                                type="button"
                                                onClick={cancelHandler}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className={`${pageClassName}__form__action__button`}
                                                type="submit"
                                            >
                                                Send
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            )}

                            {showType === "resetPassword" && (
                                <form
                                    className={`${pageClassName}__form`}
                                    onSubmit={resetPasswordHandler}
                                >
                                    <p className={`${pageClassName}__form__label`}>
                                        Reset Password
                                    </p>
                                    <div className={`${pageClassName}__form__container`}>
                                        <Input
                                            name="Project"
                                            label="Project"
                                            onChange={() => { }}
                                            value={
                                                (
                                                    payload.project_id?.value as {
                                                        label: string;
                                                        value: string;
                                                    }
                                                )?.label
                                            }
                                            isError={payload.project_id.isError}
                                            error={payload.project_id.error}
                                            readonly
                                        />
                                        <Input
                                            name="password"
                                            label="New Password"
                                            placeholder="Enter new password"
                                            onChange={changeHandler}
                                            value={payload.password.value as string}
                                            isError={payload.password.isError}
                                            error={payload.password.error}
                                        />
                                        <Input
                                            name="password_confirmation"
                                            label="Confirm Password"
                                            placeholder="Enter new password"
                                            onChange={changeHandler}
                                            value={payload.password_confirmation.value as string}
                                            isError={payload.password_confirmation.isError}
                                            error={payload.password_confirmation.error}
                                        />
                                        <div className={`${pageClassName}__form__action`}>
                                            <button
                                                className={`${pageClassName}__form__action__button`}
                                                type="submit"
                                            >
                                                Submit
                                            </button>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>
                    </Loader>
                </div>
            ) : (
                <div className={`${pageClassName}__container`}>
                    <div className={`${pageClassName}__skeleton`}>
                        <Skeleton height="extraLarge" />
                    </div>
                </div>
            )}

            {isError && (
                <div className={`${pageClassName}__container`}>
                    <div className={`${pageClassName}__notFound`}>
                        <div className={`${pageClassName}__notFound__container`}>
                            <Icon icon="sadFace" size="tertiary" />
                            <p className={`${pageClassName}__notFound__title`}>
                                {
                                    (notFoundError as unknown as { data: { message: string } })
                                        ?.data?.message
                                }
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
